import * as React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import logo from "../../images/logo-black.png";
import {
  AppBar,
  Avatar,
  Container,
  Fab,
  Fade,
  Grid,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SwipeableDrawer,
  useScrollTrigger,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const drawerWidth = 300;

const menuItems = [
  {
    primaryText: "Understanding Kingdom",
    icon: "1",
    path: "3.0/day-one",
  },
  {
    primaryText: "Kingdom of Christ",
    secondaryText: "Righteousness and Peace",
    icon: "2",
    path: "3.0/day-two",
  },
  {
    primaryText: "Kingdom of God",
    secondaryText: "Joy",
    icon: "3",
    path: "3.0/day-three",
  },
];

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  //   justifyContent: "space-between",
  background: theme.palette.primary.main,
  borderBottomLeftRadius: 6,
  borderBottomRightRadius: 6,
}));

const SpacingDiv = styled("div")(({ theme }) => ({
  ...theme.mixins.toolbar,
}));

function ScrollTop(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleClick = (event) => {
    const anchor = (event.target.ownerDocument || document).querySelector(
      "#back-to-top-anchor"
    );

    if (anchor) {
      anchor.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
      >
        {children}
      </Box>
    </Fade>
  );
}

ScrollTop.propTypes = {
  children: PropTypes.element.isRequired,
};

export default function LayoutThree(props) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleDrawer = (open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setOpen(open);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        elevation={1}
        sx={{
          backgroundColor: "background.default",
        }}
      >
        <Toolbar>
          <Grid container spacing={2}>
            <Grid item xs={2} sm={4}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={toggleDrawer(true)}
                edge="start"
                sx={{ mr: 2, ...(open && { display: "none" }) }}
              >
                <MenuIcon color="primary" />
              </IconButton>
            </Grid>
            <Grid
              item
              xs={9}
              sm={4}
              sx={{
                display: "flex",
                justifyContent: "center",
                // borderBottom: "1px solid",
                // borderBottomColor: "primary.main",
              }}
            >
              <Link href="/">
                <Box
                  component="img"
                  src={logo}
                  alt="Gadites Logo"
                  width={30}
                  height={30}
                />
              </Link>
              <Typography
                variant="h6"
                color="primary"
                noWrap
                sx={{
                  pl: 1,
                  pb: "4px",
                  textAlign: "center",
                  fontWeight: "medium",
                  alignSelf: "flex-end",
                }}
              >
                Gadites Bible Seminar
              </Typography>
            </Grid>
            <Grid item xs={1} sm={4}></Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      <SwipeableDrawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
      >
        <DrawerHeader>
          <IconButton onClick={toggleDrawer(false)}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
          <Typography variant="h6" sx={{ px: 2, color: "white", fontSize: 17 }}>
            The Everlasting Kingdom
          </Typography>
        </DrawerHeader>
        <Box
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List sx={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}>
            {menuItems.map((item, index) => (
              <ListItem
                key={item.primaryText}
                alignItems="flex-start"
                disablePadding
              >
                <ListItemButton
                  onClick={() => navigate(item.path)}
                  selected={location.pathname === item.path}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={
                        location.pathname === item.path
                          ? { width: 35, height: 35, bgcolor: "primary.main" }
                          : { width: 35, height: 35 }
                      }
                    >
                      <Typography variant="body1">{item.icon}</Typography>
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={item.primaryText}
                    secondary={item.secondaryText}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </SwipeableDrawer>

      <Box
        id="back-to-top-anchor"
        component="main"
        sx={{ p: 1, flexGrow: 1, marginLeft: `-${drawerWidth}px` }}
      >
        <SpacingDiv />
        <Container sx={{ px: { sm: 30, md: 20 }, py: 3 }}>
          <Typography
            textTransform="uppercase"
            sx={{ typography: "body2", fontWeight: "bold", pb: 2 }}
          >
            Theme: The Everlasting Kingdom
          </Typography>
          <Outlet />
        </Container>
      </Box>
      <ScrollTop {...props}>
        <Fab size="small" aria-label="scroll back to top">
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </Box>
  );
}
